import { useOverlayState } from "@heroui/react";
import { Outlet, useMatch } from "react-router";
import { AI_ROUTES } from "@/src/ai/constants/ai-routes";
import { Drawer } from "@/src/common/components/ui";
import { useAppSelector } from "@/src/common/redux/store";
import { CommunitySwitcherProvider, useCommunitySwitcher } from "@/src/community/contexts/community-switcher-context";
import { useActiveCommunity } from "@/src/community/hooks/use-active-community";
import { getActiveCommunityId, getCommunities } from "@/src/community/store/community-selectors";
import { MESSAGE_ROUTES } from "@/src/message/constants/message-routes";
import { CometChatProvider } from "@/src/message/contexts/cometchat-provider";
import { usePresence } from "@/src/presence/hooks/use-presence";
import { ImpersonationBanner } from "@/src/staff/components/impersonation-banner";
import { AppHeader } from "../components/app-header";
import { AppSidebar } from "../components/app-sidebar";
import { CommunitySwitcherDropdown, CommunitySwitcherPanel } from "../components/community-switcher";

function AppLayoutContent() {
	useActiveCommunity();
	const communities = useAppSelector(getCommunities);
	const activeCommunityId = useAppSelector(getActiveCommunityId);
	usePresence(activeCommunityId);
	const { isOpen } = useCommunitySwitcher();
	const drawerState = useOverlayState();
	const matchMessagesList = useMatch(MESSAGE_ROUTES.LIST);
	const matchMessagesDm = useMatch(MESSAGE_ROUTES.DM);
	const matchAiChat = useMatch(AI_ROUTES.CHAT);
	const isFullBleedRoute = matchMessagesList !== null || matchMessagesDm !== null || matchAiChat !== null;

	const outletContent = isFullBleedRoute ? (
		<Outlet />
	) : (
		<div className="mx-auto w-full max-w-6xl px-4 py-6">
			<Outlet />
		</div>
	);

	return (
		<div className="flex h-screen flex-col overflow-hidden">
			<ImpersonationBanner />
			<div className="flex min-h-0 flex-1 overflow-hidden">
				<CommunitySwitcherPanel communities={communities} className="hidden md:flex" />
				<div className="relative hidden flex-1 overflow-hidden md:flex">
					<AppSidebar />
					<div className="flex flex-1 flex-col overflow-hidden">
						<AppHeader />
						<main className="flex-1 overflow-auto">{outletContent}</main>
					</div>
					<div
						className={`absolute inset-0 z-50 bg-neutral-900/60 transition-opacity duration-300 dark:bg-neutral-950/40 ${isOpen ? "opacity-100" : "pointer-events-none opacity-0"}`}
					/>
				</div>

				<div className="flex flex-1 flex-col overflow-hidden md:hidden">
					<AppHeader onMenuOpen={drawerState.open} />
					<main className="flex-1 overflow-auto">{outletContent}</main>
				</div>

				<Drawer state={drawerState}>
					<Drawer.Backdrop isDismissable className="md:hidden">
						<Drawer.Content placement="left">
							<Drawer.Dialog className="w-72 max-w-none">
								<Drawer.Header>
									<CommunitySwitcherDropdown communities={communities} />
								</Drawer.Header>
								<Drawer.Body className="p-0">
									<AppSidebar onNavigate={drawerState.close} />
								</Drawer.Body>
							</Drawer.Dialog>
						</Drawer.Content>
					</Drawer.Backdrop>
				</Drawer>
			</div>
		</div>
	);
}

export function AppLayout() {
	return (
		<CommunitySwitcherProvider>
			<CometChatProvider>
				<AppLayoutContent />
			</CometChatProvider>
		</CommunitySwitcherProvider>
	);
}
